import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  SxProps,
  TextField,
  CircularProgress,
  Autocomplete,
  debounce,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  CtaButton,
  ModalContainer,
  TextInput,
} from '@tassomutuo-backoffice/ui';
import { getHsOwnerId } from '../../services/api/lead/lead';
import {
  getHsEmailInLocalStorage,
  getHsOwnerIdInLocalStorage,
  getHsUpdateAlertInLocalStorage,
  getHsUpdateAlertDateInLocalStorage,
  setHsEmailInLocalStorage,
  setHsOwnerIdInLocalStorage,
  setHsAlertInLocalStorage,
  setHsUpdateAlertDateInLocalStorage,
  setImpersonatedUserIdInLocalStorage,
  getImpersonatedUserIdInLocalStorage,
  clearImpersonationInLocalStorage,
} from '../../services/localStorage/localStorage';
import { selectUser } from '../../store/user/userSelectors';
import { useUserStore } from '../../store/user';
import { User } from '@tassomutuo-backoffice/types';
import {
  getTeamGroupMember,
  getTeamMembers,
} from '../../services/api/team/team';
import { removeDashboardFiltersInSessionStorage } from '../../services/sessionStorage/sessionStorage';
import { getImpersonationMode } from '../../utils/impersonation';
import { getUsers } from '../../services/api/users/user';
import { clearTassoQuintoSession } from '../../services/api/tasso-quinto';
import { MANAGED_LEAD_QUERY_ROOT } from '../../pages/managedLead/managedLeadQueryKeys';

interface IFormInputs {
  email: string;
}

const emptyOptions: User[] = [];

const ImpersonatesConsultant = ({ containerSx }: { containerSx?: SxProps }) => {
  const user = useUserStore(selectUser);
  const impersonationMode = getImpersonationMode(user);

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<readonly User[]>(emptyOptions);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSearchTeamMembers, setLoadingSearchTeamMembers] =
    useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [hsUser, setHsUser] = useState<{
    hsOwnerId: string | null;
    hsEmail: string | null;
    hsUpdateAlert: boolean | false;
    hsUpdateAlertDate: string | null;
  } | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<IFormInputs>({});

  const resetQueryCaches = () => {
    const queryKeys = [
      ['leadsUnderManagement'],
      ['leadsClosed'],
      ['leadsDropped'],
      ['statistics'],
      ['tasks'],
      // Every Prestiti and CdQ cache at once: they are read with the effective
      // owner's token, so none of them describes the consultant being switched
      // to. Their keys carry the owner as well, which is what makes the
      // isolation hold even between this reset and the reload that follows.
      [MANAGED_LEAD_QUERY_ROOT],
    ];

    queryKeys.forEach((queryKey) => {
      queryClient.resetQueries({ queryKey });
    });
  };

  const resolveImpersonatedUserId = useCallback(
    async (email: string, knownUserId: string | null) => {
      if (knownUserId) return knownUserId;

      let candidates: User[] = [];
      if (impersonationMode === 'admin') {
        const users = await getUsers({ page: 1, pageSize: 10, email });
        candidates = users.results;
      } else if (impersonationMode === 'team' && user?.team?.id) {
        const users = await getTeamMembers(user.team.id, {
          page: 1,
          pageSize: 10,
          searchText: email,
        });
        candidates = users.results;
      } else if (impersonationMode === 'group' && user?.ledGroups?.[0]?.id) {
        const users = await getTeamGroupMember(user.ledGroups[0].id, {
          page: 1,
          pageSize: 10,
          searchText: email,
        });
        candidates = users.results;
      }

      return (
        candidates.find(
          (candidate) => candidate.email.toLowerCase() === email.toLowerCase()
        )?.id ?? null
      );
    },
    [impersonationMode, user]
  );

  const ownerIdMutation = useMutation({
    mutationFn: async ({
      email,
      userId,
    }: {
      email: string;
      userId: string | null;
    }) => {
      const impersonatedUserId = await resolveImpersonatedUserId(email, userId);

      if (!impersonatedUserId) {
        throw new Error('Utente da impersonare non trovato');
      }

      const owner = await getHsOwnerId(email);
      return { ...owner, impersonatedUserId };
    },
    onSuccess: (data) => {
      clearTassoQuintoSession();
      setHsOwnerIdInLocalStorage(data.hsOwnerId);
      setImpersonatedUserIdInLocalStorage(data.impersonatedUserId);
      resetQueryCaches();
      removeDashboardFiltersInSessionStorage();

      setHsOwnerIdInLocalStorage(data.hsOwnerId);
      setHsEmailInLocalStorage(data.email);
      setHsAlertInLocalStorage(data.alert);
      setHsUpdateAlertDateInLocalStorage(data.updateAlertDate);
      setHsUser({
        hsOwnerId: data.hsOwnerId,
        hsEmail: data.email,
        hsUpdateAlert: data.alert,
        hsUpdateAlertDate: data.updateAlertDate,
      });

      toast.success(`Ora visualizzi le lead di ${data.email}`);
      window.location.reload();
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  useEffect(() => {
    const hsOwnerId = getHsOwnerIdInLocalStorage();
    const hsEmail = getHsEmailInLocalStorage();
    const impersonatedUserId = getImpersonatedUserIdInLocalStorage();
    const hsUpdateAlert = getHsUpdateAlertInLocalStorage();
    const hsUpdateAlertDate = getHsUpdateAlertDateInLocalStorage();
    if (hsOwnerId || hsEmail) {
      setHsUser({ hsOwnerId, hsEmail, hsUpdateAlert, hsUpdateAlertDate });
      if (!impersonatedUserId && hsEmail) {
        resolveImpersonatedUserId(hsEmail, null)
          .then((resolvedUserId) => {
            if (resolvedUserId) {
              setImpersonatedUserIdInLocalStorage(resolvedUserId);
              clearTassoQuintoSession();
            }
          })
          .catch(() => undefined);
      }
    }
  }, [resolveImpersonatedUserId]);

  const debouncedFetchUsers = useCallback(
    debounce(async (inputValue: string) => {
      setLoadingSearchTeamMembers(true);
      try {
        const query = {
          page: 1,
          pageSize: 100,
          searchText: inputValue,
        };

        let response;
        if (impersonationMode === 'group') {
          // ADR 0002: limitazione mono-gruppo deliberata — un capo gruppo che
          // guida più gruppi vede solo i membri del primo (ledGroups[0]), senza
          // errore. Per il multi-gruppo si rivedrà l'ADR (selettore di gruppo).
          const groupId = user?.ledGroups?.[0]?.id;
          if (!groupId) {
            toast.error("Nessun gruppo associato all'utente");
            return;
          }
          response = await getTeamGroupMember(groupId, query);
        } else {
          if (!user?.team?.id) {
            toast.error("Nessun team associato all'utente");
            return;
          }
          response = await getTeamMembers(user?.team?.id, query);
        }
        // Un capo gruppo non può impersonare sé stesso (è anche `leader` del
        // gruppo, quindi può comparire tra i membri): lo si esclude dalla lista.
        const results =
          impersonationMode === 'group'
            ? response.results.filter((member) => member.id !== user?.id)
            : response.results;
        setOptions(results);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingSearchTeamMembers(false);
      }
    }, 500),
    [user]
  );

  useEffect(() => {
    /*  if (inputValue === '') {
      setOptions(emptyOptions);
      return;
    }*/
    if (impersonationMode !== 'team' && impersonationMode !== 'group') {
      return;
    }
    if (!isOpen) {
      return;
    }

    debouncedFetchUsers(inputValue);
  }, [inputValue, debouncedFetchUsers, isOpen, user, impersonationMode]);

  const onSubmit: SubmitHandler<IFormInputs> = async ({ email }) => {
    try {
      setLoading(true);
      await ownerIdMutation.mutateAsync({ email, userId: selectedUserId });
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const onResetPress = () => {
    clearTassoQuintoSession();
    resetQueryCaches();
    clearImpersonationInLocalStorage();
    setSelectedUserId(null);
    setHsUser(null);
    setIsOpen(false);

    window.location.reload();
  };

  if (!impersonationMode) return null;

  return (
    <>
      <Box sx={{ ...containerSx, p: 2 }}>
        <>
          {hsUser ? (
            <Box
              sx={{
                p: 2,
                bgcolor: (th) => th.palette.primary.light,
              }}
            >
              <Typography
                fontWeight="bold"
                pb={2}
                sx={{
                  fontSize: '14px',
                  color: 'white',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                }}
              >
                Stai impersonando: {hsUser.hsEmail} ({hsUser.hsOwnerId})
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <CtaButton
                  type="button"
                  disabled={loading}
                  onClick={onResetPress}
                >
                  Resetta
                </CtaButton>
                <CtaButton
                  type="button"
                  variant="contained"
                  onClick={() => setIsOpen(true)}
                >
                  Cambia
                </CtaButton>
              </Box>
            </Box>
          ) : (
            <CtaButton
              type="button"
              variant="contained"
              sx={{ bgcolor: (th) => th.palette.primary.light }}
              onClick={() => setIsOpen(true)}
            >
              Impersona consulente
            </CtaButton>
          )}
        </>
      </Box>
      {isOpen && (
        <ModalContainer
          open={isOpen}
          handleClose={() => setIsOpen(false)}
          title=""
          mtHeader={0}
          containerSx={{ borderRadius: '30px' }}
        >
          <Box
            component="form"
            sx={{
              bgcolor: 'white',
              width: '100%',
              maxWidth: { sm: '100%', md: '400px' },
              p: 2,
            }}
            onSubmit={handleSubmit(onSubmit)}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold" pb={2}>
              Impersona consulente
            </Typography>
            {hsUser && (
              <Box>
                <Typography fontWeight="bold" pb={2}>
                  Stai impersonando: {hsUser.hsEmail} ({hsUser.hsOwnerId})
                </Typography>
              </Box>
            )}
            {impersonationMode === 'admin' && (
              <Controller
                control={control}
                name="email"
                rules={{
                  required: 'Campo obbligatorio',
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "Inserisci un'email valida",
                  },
                }}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    type="text"
                    label="Email dell'utente da impersonare"
                    helperText={errors?.email?.message}
                    error={!!errors?.email}
                    {...field}
                  />
                )}
              />
            )}
            {(impersonationMode === 'team' ||
              impersonationMode === 'group') && (
              <Autocomplete
                sx={{ width: '100%', mb: 6, mt: 2 }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.email}
                options={options}
                loading={loadingSearchTeamMembers}
                filterOptions={(x) => x}
                noOptionsText="Nessun utente"
                onChange={(event, newValue) => {
                  setSelectedUserId(newValue?.id ?? null);
                  if (newValue) {
                    setValue('email', newValue.email);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cerca consulente"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSearchTeamMembers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            <Box display="flex" flexWrap="wrap" gap={2}>
              {hsUser && (
                <CtaButton
                  type="button"
                  disabled={loading}
                  onClick={onResetPress}
                >
                  Resetta
                </CtaButton>
              )}
              <CtaButton type="submit" variant="contained" loading={loading}>
                Impersona
              </CtaButton>
            </Box>
          </Box>
        </ModalContainer>
      )}
    </>
  );
};

export default ImpersonatesConsultant;
